import { useState, type ReactElement } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { QueriesGapTable, queriesQuery } from '~/features/help-kb'

/**
 * The questions tab — the content gap report.
 *
 * Defaults to UNANSWERED over the last 30 days, because that list is the reason
 * the tab exists. Everything else is available by switching the filter, but the
 * first thing an author should see is what nobody could answer.
 */
export const Route = createFileRoute('/admin/help-kb/queries')({
  component: HelpKbQueriesPage,
})

const DAY_RANGES = [7, 30, 90] as const

function HelpKbQueriesPage(): ReactElement {
  const [unanswered, setUnanswered] = useState(true)
  const [days, setDays] = useState<number>(30)
  const navigate = useNavigate()

  const list = useQuery(queriesQuery({ unanswered, days }))

  return (
    <>
      <div className="ka-filter-strip" role="group" aria-label="Question filters">
        <div className="ka-filter-group">
          <button
            type="button"
            className={`ka-detail-chip${unanswered ? ' is-active' : ''}`}
            aria-pressed={unanswered}
            onClick={() => setUnanswered(true)}
          >
            Unanswered
          </button>
          <button
            type="button"
            className={`ka-detail-chip${unanswered ? '' : ' is-active'}`}
            aria-pressed={!unanswered}
            onClick={() => setUnanswered(false)}
          >
            All questions
          </button>
        </div>

        <div className="ka-filter-group ka-filter-group--no-divider">
          <label htmlFor="help-kb-days" className="ka-filter-group-label">Period</label>
          <select
            id="help-kb-days"
            className="ka-filter-select"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            {DAY_RANGES.map((range) => (
              <option key={range} value={range}>Last {range} days</option>
            ))}
          </select>
        </div>
      </div>

      <QueriesGapTable
        queries={list.data?.queries ?? []}
        isLoading={list.isLoading}
        error={list.error}
        onCreateArticle={() => { void navigate({ to: '/admin/help-kb' }) }}
      />
    </>
  )
}
